import { Button } from "@/components/ui/button";
import { ToofiMark } from "@/components/ui/toofi-mark";
import { getToofiModeMeta } from "@/lib/toofi-modes";
import type { ToofiProductMode } from "@/types/agentic-chat";
import { FileText } from "lucide-react";

type ReaderAiEmptyStateProps = {
  surfaceKind?: "reader" | "workspace";
  productMode?: ToofiProductMode | null;
  onUseFileContext?: () => void;
};

export function ReaderAiEmptyState({
  surfaceKind = "reader",
  productMode = null,
  onUseFileContext,
}: ReaderAiEmptyStateProps) {
  const modeLabel = productMode ? getToofiModeMeta(productMode).label : null;
  const isWorkspace = surfaceKind === "workspace";
  const eyebrow = isWorkspace ? (modeLabel ? `${modeLabel} mode` : "Workspace chat") : "Reader context";
  const title = isWorkspace
    ? modeLabel
      ? `Start a ${modeLabel.toLowerCase()} chat`
      : "Start a Toofi chat"
    : "Start with a selection";
  const body = isWorkspace
    ? modeLabel
      ? `Use this ${modeLabel.toLowerCase()} thread for your study question, plan, or next step.`
      : "Ask about your study, what to do next, or the help you need right now."
    : "Highlight text for precise help, or use this file when you want broader answers from the PDF.";

  return (
    <div className="relative flex w-full max-w-[20rem] flex-col items-center overflow-hidden rounded-[1.6rem] border border-white/80 bg-[linear-gradient(180deg,rgba(255,255,255,0.94),rgba(241,245,249,0.9))] px-5 py-5 text-center shadow-[0_22px_52px_rgba(148,163,184,0.14)] backdrop-blur-2xl">
      <div className="pointer-events-none absolute left-1/2 top-0 h-16 w-36 -translate-x-1/2 rounded-full bg-blue-100/45 blur-2xl" />
      <ToofiMark
        className="h-10 w-10 [&_svg]:!h-8 [&_svg]:!w-8"
        transparent
      />
      <div className="mt-3 inline-flex items-center rounded-full border border-slate-200/80 bg-white/80 px-2.5 py-1 text-[9px] font-semibold uppercase tracking-[0.18em] text-slate-500">
        {eyebrow}
      </div>
      <h4 className="mt-3 text-[15px] font-semibold tracking-[-0.02em] text-slate-900">{title}</h4>
      <p className="mt-2 max-w-[16rem] text-[12px] leading-5 text-slate-500">
        {body}
      </p>
      {!isWorkspace && onUseFileContext ? (
        <div className="mt-4 flex w-full max-w-[16rem] justify-center">
          <Button
            type="button"
            variant="secondary"
            className="h-9 w-full gap-1.5 rounded-full px-3 text-[10px] font-semibold"
            onClick={onUseFileContext}
          >
            <FileText className="h-3.5 w-3.5" />
            Use this file
          </Button>
        </div>
      ) : null}
    </div>
  );
}
